(() => {
  if (customElements.get('am-campaign-slideshow')) return;

  // Existing campaigns remain sale-aware when their older markup has no dates.
  // New banners can declare data-sale-start/data-sale-end (ISO dates with an offset).
  const saleWindows = {
    unitronic: { start: '2026-09-29T00:02:00-04:00', end: '2026-10-17T00:00:00-04:00' },
    cts: { start: '2026-10-03T11:55:00-04:00', end: '2026-10-31T00:00:00-04:00' }
  };

  const isActiveSale = (slide, now) => {
    // Explicit dates take precedence, including empty/invalid dates (no priority).
    const declared = slide.hasAttribute('data-sale-start') || slide.hasAttribute('data-sale-end');
    const sale = declared ? { start: slide.dataset.saleStart, end: slide.dataset.saleEnd } : saleWindows[slide.dataset.slideId];
    if (!sale?.end) return false;
    const start = sale.start ? Date.parse(sale.start) : -Infinity;
    const end = Date.parse(sale.end);
    return !Number.isNaN(start) && Number.isFinite(end) && start <= now && now < end;
  };

  const shuffle = (slides) => {
    for (let index = slides.length - 1; index > 0; index--) {
      const other = Math.floor(Math.random() * (index + 1));
      [slides[index], slides[other]] = [slides[other], slides[index]];
    }
    return slides;
  };

  class AMCampaignSlideshow extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.track = this.querySelector('.am-campaigns__items');
      this.querySelectorAll('[data-slide-clone]').forEach((clone) => clone.remove());
      this.slides = [...this.querySelectorAll('[data-slide-id]')];
      if (!this.track || this.slides.length < 2) return;

      this.abort = new AbortController();
      const on = (target, event, handler) => target.addEventListener(event, handler, { signal: this.abort.signal });
      this.index = 0;
      this.focused = false;
      this.editorSelected = false;
      this.animating = false;
      this.pendingIndex = null;
      this.gesture = null;
      this.interval = Math.max(4000, Number(this.dataset.interval) || 7000);
      this.dots = [...this.querySelectorAll('[data-slide]')];
      // Shuffle active sales first, then other banners, once per page load.
      // Move each whole card and its matching dot; editor order stays stable.
      if (this.dataset.shuffle !== 'false' && !window.Shopify?.designMode) {
        const dotsById = new Map(this.slides.map((slide, index) => [slide.dataset.slideId, this.dots[index]]));
        const now = Date.now();
        const sales = this.slides.filter((slide) => isActiveSale(slide, now));
        const rest = this.slides.filter((slide) => !sales.includes(slide));
        this.slides = [...shuffle(sales), ...shuffle(rest)];
        this.dots = this.slides.map((slide) => dotsById.get(slide.dataset.slideId));
        this.track.append(...this.slides);
        this.querySelector('.am-campaigns__dots').append(...this.dots);
        this.slides.forEach((slide, index) => {
          slide.setAttribute('aria-label', `${index + 1} of ${this.slides.length}`);
          this.dots[index].dataset.slide = index;
          slide.querySelectorAll('img').forEach((image) => {
            image.loading = index === 0 ? 'eager' : 'lazy';
            image.fetchPriority = index === 0 ? 'high' : 'auto';
          });
        });
      }
      // End copies make the last -> first transition a single forward glide.
      const cloneSlide = (slide) => {
        const clone = slide.cloneNode(true);
        clone.removeAttribute('data-slide-id');
        clone.removeAttribute('data-shopify-editor-block');
        clone.removeAttribute('data-pinned-slide');
        clone.setAttribute('data-slide-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
        clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
        clone.querySelectorAll('img').forEach((image) => { image.loading = 'eager'; image.fetchPriority = 'auto'; });
        return clone;
      };
      this.track.prepend(cloneSlide(this.slides[this.slides.length - 1]));
      this.track.append(cloneSlide(this.slides[0]));
      this.classList.add('is-ready');
      this.querySelector('.am-campaigns__controls').hidden = false;

      on(this, 'click', (event) => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.hasAttribute('data-slide')) this.show(Number(button.dataset.slide));
        if (button.dataset.action === 'previous') this.show(this.index - 1);
        if (button.dataset.action === 'next') this.show(this.index + 1);
      });
      on(this, 'keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        // Keep keyboard focus visible when navigating from a slide's link.
        if (this.track.contains(document.activeElement)) this.querySelector('[data-action="next"]').focus();
        this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      });
      on(this, 'focusin', (event) => {
        this.focused = this.track.contains(event.target);
        this.schedule();
      });
      on(this, 'focusout', (event) => {
        this.focused = this.track.contains(event.relatedTarget);
        this.schedule();
      });
      on(document, 'visibilitychange', () => this.schedule());
      on(this.track, 'transitionend', (event) => {
        if (event.target === this.track && event.propertyName === 'transform') this.finishTransition();
      });

      const viewport = this.querySelector('.am-campaigns__viewport');
      const settleGesture = () => {
        const dragging = this.gesture?.dragging;
        this.gesture = null;
        this.track.style.transition = '';
        if (dragging) {
          this.ignoreClickUntil = Date.now() + 500;
          this.animating = true;
          this.positionTrack(this.index + 1, true);
          this.transitionTimer = setTimeout(() => this.finishTransition(), 1100);
        }
        this.schedule();
      };
      on(viewport, 'pointerdown', (event) => {
        if (this.animating || this.gesture || event.isPrimary === false || (event.pointerType !== 'touch' && event.pointerType !== 'pen')) return;
        this.gesture = { x: event.clientX, y: event.clientY, pointerId: event.pointerId, time: event.timeStamp, dragging: false };
        this.schedule();
      });
      on(viewport, 'pointermove', (event) => {
        if (!this.gesture || event.pointerId !== this.gesture.pointerId) return;
        const dx = event.clientX - this.gesture.x;
        const dy = event.clientY - this.gesture.y;
        if (!this.gesture.dragging) {
          if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) { settleGesture(); return; }
          if (Math.abs(dx) < 8 || Math.abs(dx) <= Math.abs(dy) * 1.25) return;
          this.gesture.dragging = true;
          viewport.setPointerCapture(event.pointerId);
          this.track.style.transition = 'none';
        }
        const offset = Math.max(-viewport.clientWidth, Math.min(viewport.clientWidth, dx));
        this.track.style.transform = `translateX(calc(-${(this.index + 1) * 100}% + ${offset}px))`;
      });
      on(viewport, 'pointerup', (event) => {
        if (!this.gesture || event.pointerId !== this.gesture.pointerId) return;
        const dx = event.clientX - this.gesture.x;
        const dy = event.clientY - this.gesture.y;
        const flick = Math.abs(dx) > 20 && event.timeStamp - this.gesture.time < 250;
        const threshold = Math.min(60, viewport.clientWidth * 0.15);
        if (this.gesture.dragging) this.ignoreClickUntil = Date.now() + 500;
        if ((Math.abs(dx) > threshold || flick) && Math.abs(dx) > Math.abs(dy) * 1.5) {
          this.gesture = null;
          this.track.style.transition = '';
          this.ignoreClickUntil = Date.now() + 500;
          this.show(this.index + (dx < 0 ? 1 : -1));
        } else settleGesture();
      });
      on(viewport, 'pointercancel', (event) => {
        if (event.pointerId === this.gesture?.pointerId) settleGesture();
      });
      on(viewport, 'lostpointercapture', (event) => {
        // Touch starts with implicit capture on the image/link. Its loss bubbles
        // here when capture moves to the viewport; the swipe must keep going.
        if (event.target === viewport && event.pointerId === this.gesture?.pointerId) settleGesture();
      });
      on(viewport, 'pointerleave', () => {
        if (this.gesture && !this.gesture.dragging) settleGesture();
      });
      on(viewport, 'click', (event) => {
        if (Date.now() < (this.ignoreClickUntil || 0)) { event.preventDefault(); event.stopPropagation(); }
      });

      // Custom-element lifecycle handles section reloads without touching other apps.
      on(this, 'shopify:block:select', (event) => {
        const selected = this.slides.findIndex((slide) => slide.dataset.slideId === event.detail.blockId);
        if (selected !== -1) { this.editorSelected = true; this.finishTransition(); this.show(selected); }
      });
      on(this, 'shopify:block:deselect', () => { this.editorSelected = false; this.schedule(); });
      this.show(0, false);
    }

    show(index, animate = true) {
      if (this.animating) { this.pendingIndex = index; return; }
      const nextIndex = (index + this.slides.length) % this.slides.length;
      this.animating = animate && nextIndex !== this.index;
      this.index = nextIndex;
      this.loopReset = index < 0 || index >= this.slides.length;
      this.positionTrack(index + 1, this.animating);
      if (this.animating) this.transitionTimer = setTimeout(() => this.finishTransition(), 1100);
      this.slides.forEach((slide, position) => {
        const active = position === this.index;
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
        slide.querySelectorAll('a').forEach((link) => active ? link.removeAttribute('tabindex') : link.setAttribute('tabindex', '-1'));
        if (active) this.dots[position].setAttribute('aria-current', 'true');
        else this.dots[position].removeAttribute('aria-current');
      });
      this.querySelector('[data-current-slide]').textContent = this.index + 1;
      this.schedule();
    }

    positionTrack(position, animate) {
      if (!animate) this.track.style.transition = 'none';
      this.track.style.transform = `translateX(-${position * 100}%)`;
      if (!animate) {
        // Commit the identical end frame before restoring the normal transition.
        void this.track.offsetWidth;
        this.track.style.transition = '';
      }
    }

    finishTransition() {
      clearTimeout(this.transitionTimer);
      if (this.loopReset) this.positionTrack(this.index + 1, false);
      this.loopReset = false;
      this.animating = false;
      if (this.pendingIndex !== null) {
        const next = this.pendingIndex;
        this.pendingIndex = null;
        this.show(next);
      }
    }

    schedule() {
      clearTimeout(this.timer);
      const stopped = this.dataset.autoplay === 'false' || this.focused || this.editorSelected || this.gesture || document.hidden;
      this.track.setAttribute('aria-live', stopped ? 'polite' : 'off');
      if (!stopped) this.timer = setTimeout(() => this.show(this.index + 1), this.interval);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.transitionTimer);
      this.abort?.abort();
      this.abort = null;
    }
  }

  customElements.define('am-campaign-slideshow', AMCampaignSlideshow);
})();
